import {
  type BuffSnapshot,
  getSpellTemplate,
} from "@vinyl/shared";
import { el } from "./dom.js";

// Top-right buff tracker for the local player. Each active buff renders as
// a small chip with its name and a countdown timer; chips disappear when the
// expiresAt passes (server emits buffUpdate with the buff stripped). The
// world-sprite glow lives elsewhere (WorldView), this is purely UI chrome.
const TICK_INTERVAL_MS = 250;

interface ChipState {
  spellId: string;
  expiresAt: number;
  root: HTMLElement;
  timerEl: HTMLElement;
}

export class BuffTracker {
  readonly root: HTMLElement;
  private buffs: BuffSnapshot[] = [];
  private chips: ChipState[] = [];
  private intervalId: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this.root = el("div", { class: "buff-tracker" });
    this.intervalId = setInterval(() => this.refreshTimers(), TICK_INTERVAL_MS);
  }

  // Called when the local player's buffUpdate arrives. Replaces the list
  // wholesale — the wire payload is already the authoritative state.
  setBuffs(buffs: BuffSnapshot[]): void {
    this.buffs = buffs;
    this.render();
  }

  destroy(): void {
    if (this.intervalId !== null) clearInterval(this.intervalId);
    this.intervalId = null;
    this.root.remove();
  }

  private render(): void {
    this.root.replaceChildren();
    this.chips = [];
    for (const buff of this.buffs) {
      const tpl = getSpellTemplate(buff.spellId);
      const name = tpl?.name ?? buff.spellId;
      const timerEl = el("span", { class: "buff-chip-timer" }, "—");
      const chipChildren: (HTMLElement | string)[] = [
        el("span", { class: "buff-chip-name" }, name),
      ];
      // Absorb buffs show pool remaining inline so the player can see the
      // shield deplete in real time without hovering for a tooltip.
      if (
        buff.kind === "absorb" &&
        typeof buff.absorbRemaining === "number" &&
        typeof buff.absorbInitial === "number"
      ) {
        chipChildren.push(
          el(
            "span",
            { class: "buff-chip-pool" },
            `${Math.round(buff.absorbRemaining)} / ${Math.round(buff.absorbInitial)}`,
          ),
        );
      }
      chipChildren.push(timerEl);
      const chip = el(
        "div",
        {
          class: `buff-chip school-${tpl?.school ?? "arcane"} kind-${buff.kind}`,
          title: tooltipFor(buff, name),
        },
        ...chipChildren,
      );
      this.root.append(chip);
      this.chips.push({
        spellId: buff.spellId,
        expiresAt: buff.expiresAt,
        root: chip,
        timerEl,
      });
    }
    this.refreshTimers();
  }

  private refreshTimers(): void {
    const now = Date.now();
    for (const chip of this.chips) {
      const remainMs = Math.max(0, chip.expiresAt - now);
      chip.timerEl.textContent = formatRemain(remainMs);
    }
  }
}

function formatRemain(ms: number): string {
  if (ms <= 0) return "0s";
  const totalSeconds = Math.ceil(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}m${s.toString().padStart(2, "0")}`;
}

function tooltipFor(buff: BuffSnapshot, name: string): string {
  const lines: string[] = [name];
  if (buff.kind === "buffDmg" && typeof buff.dmgPct === "number") {
    lines.push(`+${Math.round(buff.dmgPct * 100)}% damage`);
  }
  if (buff.kind === "absorb" && typeof buff.absorbRemaining === "number") {
    const max = buff.absorbInitial ?? buff.absorbRemaining;
    lines.push(`Shield ${Math.round(buff.absorbRemaining)} / ${Math.round(max)}`);
  }
  return lines.join("\n");
}
